import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { deleteWorkbook } from './workbook-operations';

interface DeleteProjectResult {
  success: boolean;
  deleted_project_id: string | null;
  deleted_goal_id: string | null;
  deleted_pages_count: number;
  deleted_attachments_count: number;
  error_message: string;
}

/**
 * Hook for deleting a project and its associated goal atomically
 * Uses a simpler approach: delete goal first, then use existing workbook deletion
 */
export function useDeleteProjectWithGoal() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (projectId: string): Promise<DeleteProjectResult> => {
      let deletedGoalId: string | null = null;
      let pagesCount = 0;
      let attachmentsCount = 0;

      try {
        // First, get the associated goal (if any) and counts for reporting
        const { data: goalData, error: goalQueryError } = await supabase
          .from('planner_goals_workbooks')
          .select(`
            goal_id,
            planner_goals (id, title)
          `)
          .eq('workbook_id', projectId)
          .maybeSingle(); // Use maybeSingle instead of single to handle no results gracefully

        // Don't throw error if no goal found - this is expected for some projects
        if (goalQueryError && goalQueryError.code !== 'PGRST116') {
          console.warn('[DeleteProjectWithGoal] Error fetching goal data:', goalQueryError);
        }

        // Count pages and research attachments for reporting
        const { count: pageCount } = await supabase
          .from('workbook_page_content')
          .select('id', { count: 'exact', head: true })
          .eq('workbook_id', projectId);
        pagesCount = pageCount || 0;

        // Count research attachments from pages
        if (pagesCount > 0) {
          const { data: pageIds } = await supabase
            .from('workbook_page_content')
            .select('id')
            .eq('workbook_id', projectId);

          if (pageIds && pageIds.length > 0) {
            const { count } = await supabase
              .from('workbook_page_research_attachments')
              .select('id', { count: 'exact', head: true })
              .in('page_content_id', pageIds.map(p => p.id));
            attachmentsCount = count || 0;
          }
        }

        // Delete the associated goal first (if exists)
        if (goalData?.planner_goals) {
          deletedGoalId = goalData.planner_goals.id;
          
          const { error: goalDeleteError } = await supabase
            .from('planner_goals')
            .delete()
            .eq('id', deletedGoalId);

          if (goalDeleteError) {
            throw new Error(`Failed to delete associated goal: ${goalDeleteError.message}`);
          }

          // 🔧 FIX: Clean up orphaned thread metadata after goal deletion
          // This prevents 406 errors when threads reference deleted goals
          try {
            const { error: metadataCleanupError } = await supabase
              .from('agent_threads')
              .update({ 
                metadata: supabase.sql`metadata - 'goalId' - 'projectContext'`,
                updated_at: new Date().toISOString()
              })
              .eq('metadata->>goalId', deletedGoalId);

            if (metadataCleanupError) {
              console.warn('[DeleteProjectWithGoal] Failed to clean thread metadata:', metadataCleanupError);
              // Non-critical error - don't block the deletion
            } else {
              console.log('[DeleteProjectWithGoal] Cleaned up thread metadata for deleted goal:', deletedGoalId);
            }
          } catch (cleanupError) {
            console.warn('[DeleteProjectWithGoal] Thread metadata cleanup failed (non-critical):', cleanupError);
          }
        }

        // Now delete the workbook using the existing function
        await deleteWorkbook(projectId);

        return {
          success: true,
          deleted_project_id: projectId,
          deleted_goal_id: deletedGoalId,
          deleted_pages_count: pagesCount,
          deleted_attachments_count: attachmentsCount,
          error_message: 'Project and associated goal deleted successfully'
        };

      } catch (error: any) {
        throw new Error(error.message || 'Failed to delete project and associated data');
      }
    },
    onMutate: async (projectId: string) => {
      // Cancel all outgoing queries for this project to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['project-navigation', projectId] });
      await queryClient.cancelQueries({ queryKey: ['workbook-goal', projectId] });
      await queryClient.cancelQueries({ queryKey: ['goal-workbook'] });
      await queryClient.cancelQueries({ queryKey: ['workbook-projects'] });
      
      // Remove project-specific queries immediately to prevent 406 errors
      queryClient.removeQueries({ queryKey: ['project-navigation', projectId] });
      queryClient.removeQueries({ queryKey: ['workbook-goal', projectId] });
      
      // Store the projectId for cleanup in case of error
      return { projectId };
    },
    onSuccess: (result) => {
      console.log('[DeleteProjectWithGoal] Success:', result);
      
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-workbook-junctions'] });
      queryClient.invalidateQueries({ queryKey: ['workbook-goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-workbooks'] });
      
      // Remove any remaining project-specific queries
      if (result.deleted_project_id) {
        queryClient.removeQueries({ queryKey: ['project-navigation', result.deleted_project_id] });
        queryClient.removeQueries({ queryKey: ['workbook-goal', result.deleted_project_id] });
      }
      
      // Build detailed success message
      let message = `Project deleted successfully`;
      
      if (result.deleted_goal_id) {
        message += ` (including associated goal)`;
      }
      
      const details = [];
      if (result.deleted_pages_count > 0) {
        details.push(`${result.deleted_pages_count} page${result.deleted_pages_count > 1 ? 's' : ''}`);
      }
      if (result.deleted_attachments_count > 0) {
        details.push(`${result.deleted_attachments_count} research attachment${result.deleted_attachments_count > 1 ? 's' : ''}`);
      }
      
      if (details.length > 0) {
        message += `. Also cleaned up: ${details.join(', ')}.`;
      }

      toast({
        title: "Success",
        description: message,
        variant: "success"
      });
    },
    onError: (error: any, projectId: string, context) => {
      console.error('[DeleteProjectWithGoal] Error:', error);
      
      // If we have context, we can restore some queries
      if (context?.projectId) {
        // Invalidate queries to restore accurate data
        queryClient.invalidateQueries({ queryKey: ['workbook-projects'] });
        queryClient.invalidateQueries({ queryKey: ['project-navigation', context.projectId] });
        queryClient.invalidateQueries({ queryKey: ['goals'] });
      }
      
      toast({
        title: "Error", 
        description: error.message || "Failed to delete project and associated data",
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook for checking what would be deleted (preview mode)
 * Useful for confirmation dialogs showing impact
 */
export function useProjectDeletionPreview() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      // Get project details
      const { data: project } = await supabase
        .from('workbook')
        .select('id, title')
        .eq('id', projectId)
        .single();

      // Get associated goal
      const { data: goalData } = await supabase
        .from('planner_goals_workbooks')
        .select(`
          planner_goals (id, title)
        `)
        .eq('workbook_id', projectId)
        .single();

      // Count pages
      const { count: pagesCount } = await supabase
        .from('workbook_page_content')
        .select('id', { count: 'exact', head: true })
        .eq('workbook_id', projectId);

             // Count research attachments from pages (workbook-level research was removed)
       // First get all page IDs for this project
       const { data: pageIds } = await supabase
         .from('workbook_page_content')
         .select('id')
         .eq('workbook_id', projectId);

       let attachmentsCount = 0;
       if (pageIds && pageIds.length > 0) {
         const pageIdList = pageIds.map(p => p.id);
         const { count } = await supabase
           .from('workbook_page_research_attachments')
           .select('id', { count: 'exact', head: true })
           .in('page_content_id', pageIdList);
         attachmentsCount = count || 0;
       }

      // Count tasks (if goal exists)
      let tasksCount = 0;
      if (goalData?.planner_goals) {
        const { count } = await supabase
          .from('planner_tasks')
          .select('id', { count: 'exact', head: true })
          .eq('goal_id', goalData.planner_goals.id);
        tasksCount = count || 0;
      }

      return {
        project,
        goal: goalData?.planner_goals || null,
        counts: {
          pages: pagesCount || 0,
          attachments: attachmentsCount || 0,
          tasks: tasksCount
        }
      };
    }
  });
} 